import {
  AuthLayout,
  Button,
  Checkbox,
  Field,
  Input,
  Label,
  PasswordInput,
  Shake,
  applyFieldErrors,
  errorMessage,
} from '@bixo/ui'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { getRouteApi, useNavigate } from '@tanstack/react-router'
import { AlertCircle, ArrowRight, ShieldCheck } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'
import { tenantBadges, tenantFeatures, useTenantBrand } from '../components/auth-brand'
import { TenantShowcase } from '../components/auth-showcase'
import { endpoints, qk } from '../lib/api'

const schema = z.object({
  email: z.email('Enter a valid email address'),
  password: z.string().min(1, 'Enter your password'),
  remember: z.boolean(),
})

type FormValues = z.infer<typeof schema>

const route = getRouteApi('/login')

export function LoginPage() {
  const { tenant, brand } = useTenantBrand()
  const { redirect } = route.useSearch()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [formError, setFormError] = useState<string | null>(null)
  const [failures, setFailures] = useState(0)

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', password: '', remember: true },
  })

  const login = useMutation({
    mutationFn: endpoints.login,
    onSuccess: async (result) => {
      if (result.two_factor) {
        await navigate({ to: '/two-factor', search: redirect ? { redirect } : {} })
        return
      }
      queryClient.setQueryData(qk.me, result.user)
      await navigate({ to: redirect ?? '/' })
    },
    onError: (error) => {
      setFailures((n) => n + 1)
      if (!applyFieldErrors(error, form.setError, ['email', 'password'] as const)) {
        setFormError(errorMessage(error))
      }
    },
  })

  const onSubmit = form.handleSubmit(
    (values) => {
      setFormError(null)
      login.mutate(values)
    },
    () => setFailures((n) => n + 1),
  )

  return (
    <AuthLayout
      brand={brand}
      title="Welcome back"
      subtitle={`Sign in to ${brand.name} to continue.`}
      footer={
        <span className="inline-flex items-center gap-1.5">
          <ShieldCheck className="size-3.5" /> Protected with encrypted sessions and optional two-factor
          sign-in
        </span>
      }
      showcase={
        <TenantShowcase
          eyebrow={tenant?.name ?? 'Your business'}
          title="Your whole business, in one beautifully simple place."
        />
      }
      features={tenantFeatures}
      badges={tenantBadges}
    >
      <Shake trigger={failures}>
        <form onSubmit={onSubmit} className="grid gap-5" noValidate>
          <AnimatePresence initial={false}>
            {formError && (
              <motion.div
                role="alert"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="overflow-hidden"
              >
                <div className="flex items-start gap-2.5 rounded-lg border border-danger/25 bg-danger-soft px-3 py-2.5 text-sm text-danger">
                  <AlertCircle className="mt-0.5 size-4 shrink-0" />
                  {formError}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <Field label="Email" error={form.formState.errors.email?.message}>
            {(control) => (
              <Input
                {...control}
                type="email"
                autoComplete="username"
                autoFocus
                placeholder="you@company.com"
                className="h-11"
                {...form.register('email')}
              />
            )}
          </Field>

          <Field label="Password" error={form.formState.errors.password?.message}>
            {(control) => (
              <PasswordInput
                {...control}
                autoComplete="current-password"
                placeholder="••••••••"
                {...form.register('password')}
              />
            )}
          </Field>

          <div className="flex items-center gap-2">
            <Controller
              control={form.control}
              name="remember"
              render={({ field }) => (
                <Checkbox
                  id="remember"
                  checked={field.value}
                  onCheckedChange={(v) => field.onChange(v === true)}
                />
              )}
            />
            <Label htmlFor="remember" className="font-normal text-muted-foreground">
              Keep me signed in on this device
            </Label>
          </div>

          <Button type="submit" size="lg" loading={login.isPending} className="group w-full">
            Sign in
            <ArrowRight className="transition-transform group-hover:translate-x-0.5" />
          </Button>
        </form>
      </Shake>
    </AuthLayout>
  )
}
